import React, {useEffect, useState} from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Box,
  Flex,
  Text,
  Icon,
  Avatar,
  Badge,
} from "@chakra-ui/react";
import {FiChevronDown, FiUsers} from "react-icons/fi";
import {useNavigate} from "react-router-dom";
import {fetchDependents} from "../api/dependents";

// Switching to a dependent's profile.
//
// The list comes from the SERVER, and so does what each entry may reach. This
// used to hold `accessConfig` — two booleans — and decide the navigation from
// them in the browser. That could not express an age band, an expired court
// order or the dependent's own restriction, and it put the decision on the
// wrong side of the wire.
//
// The dependent's own name and date of birth always travel, whatever the
// profile grade says, because a switcher of unlabelled buttons is how somebody
// books an appointment for the wrong child. A guardianship that grants nothing
// anywhere does not appear here at all.
//
// Switching loads NOTHING. It records which dependent is active and navigates;
// the screen that mounts then fetches from an endpoint that names that
// dependent in the path. What stood here re-ran the whole patient bootstrap
// with the dependent's pid — a batch of generic database reads against somebody
// else's record, which no permission grade could narrow and no response could
// redact.

const LEVEL_LABEL = {
  full: null, // nothing to say: this is the ordinary case
  limited: "Limited",
  restricted: "Restricted",
};

export default function ProfileSwitcher({state, dispatch}) {
  const navigate = useNavigate();
  const [dependents, setDependents] = useState(state?.guardianOf || []);

  const guardiansEnabled = state?.organization?.features?.guardians;
  const viewingDependent = !!state?.viewingDependent;
  const activePid = state?.activeDependent?.dependentPatientId || null;

  // The login response carries the same list, shaped by the same engine — this
  // refresh exists because an age band, a revocation or an expiry can land
  // between signing in and reaching for the switcher.
  useEffect(() => {
    if (!guardiansEnabled || !state?.jwt) return;
    let cancelled = false;
    fetchDependents(state)
      .then((list) => !cancelled && setDependents(list))
      // A stale list is a smaller harm than a broken sidebar, and every entry
      // is re-checked server-side the moment it is used.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [guardiansEnabled, state?.jwt]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!guardiansEnabled || dependents.length === 0) return null;

  const accountHolder = state?.patient;
  const activeEntry = dependents.find((d) => d.dependentPatientId === activePid);
  const activeLabel = viewingDependent
    ? nameOf(activeEntry) || "Dependent"
    : "My profile";

  const handleSelectSelf = () => {
    if (!viewingDependent) return;
    // Nothing to reload: the account holder's own data was never replaced.
    navigate("/", {state: {...state, viewingDependent: false, activeDependent: null}});
  };

  const handleSelectDependent = (entry) => {
    navigate("/", {state: {...state, viewingDependent: true, activeDependent: entry}});
  };

  return (
    <Box px="3" pt="3" pb="2" borderBottom="1px" borderBottomColor="#e6e6e6">
      <Menu placement="bottom-start" closeOnBlur>
        <MenuButton
          w="full"
          borderRadius="8px"
          px="2"
          py="2"
          border="1px solid"
          borderColor="#e6e6e6"
          bg={viewingDependent ? "orange.50" : "white"}
          _hover={{bg: viewingDependent ? "orange.100" : "#f8f9fa"}}
          _focus={{boxShadow: "none"}}
          transition="all 0.15s ease"
        >
          <Flex alignItems="center" justifyContent="space-between" gap="2">
            <Flex alignItems="center" gap="2" minW={0} flex={1}>
              <Icon as={FiUsers} color="#6b7280" fontSize="14px" />
              <Text
                fontSize="13px"
                fontWeight="500"
                color="#1a1f36"
                noOfLines={1}
                textAlign="left"
              >
                {activeLabel}
              </Text>
              {viewingDependent && (
                <Badge colorScheme="orange" fontSize="9px">
                  Dependent
                </Badge>
              )}
            </Flex>
            <Icon as={FiChevronDown} color="#6b7280" fontSize="14px" />
          </Flex>
        </MenuButton>
        <MenuList minW="260px" zIndex={9999} shadow="lg" borderRadius="8px" py="1">
          <MenuItem
            onClick={handleSelectSelf}
            fontSize="13px"
            icon={
              <Avatar size="xs" name={accountHolder?.fName || "Me"} bg="#1a1f36" color="white" />
            }
            bg={!viewingDependent ? "#f0f9ff" : "transparent"}
          >
            <Flex direction="column">
              <Text fontWeight="500">My profile</Text>
              <Text fontSize="11px" color="gray.500">
                {accountHolder?.fName} {accountHolder?.lName}
              </Text>
            </Flex>
          </MenuItem>
          <MenuDivider />
          {dependents.map((entry) => {
            const isActive = viewingDependent && activePid === entry.dependentPatientId;
            const levelNote = LEVEL_LABEL[entry.accessLevel];
            return (
              <MenuItem
                key={entry.dependentPatientId}
                onClick={() => handleSelectDependent(entry)}
                fontSize="13px"
                icon={
                  <Avatar
                    size="xs"
                    name={nameOf(entry) || "Dep"}
                    bg="orange.500"
                    color="white"
                  />
                }
                bg={isActive ? "#fff7ed" : "transparent"}
              >
                <Flex direction="column">
                  <Text fontWeight="500">{nameOf(entry)}</Text>
                  <Text fontSize="11px" color="gray.500">
                    {relationshipLabel(entry.relationshipType)}
                    {levelNote ? ` · ${levelNote} access` : ""}
                  </Text>
                </Flex>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </Box>
  );
}

function nameOf(entry) {
  const identity = entry?.identity;
  if (!identity) return null;
  return `${identity.fName || ""} ${identity.lName || ""}`.trim() || null;
}

function relationshipLabel(type) {
  if (!type) return "Dependent";
  return type
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
